/**
 * Plans API endpoints - community plans, discovery feeds, intel
 */
import { fetchApi } from './apiBase';
import {
  CommunityPlanListItem,
  CommunityPlanDetail,
  CrowdWisdom,
  DestinationIntel,
  Itinerary,
} from '../types';

// === Types ===

export interface PlansFilterParams {
  trip_type?: string;
  distance?: string;
  group_type?: string;
  season?: string;
  search?: string;
  sort?: 'score' | 'newest' | 'most_used';
}

export interface IntelSubscribeData {
  email: string;
  match_me_answers?: Record<string, unknown> | null;
}

export interface IntelSubscribeResponse {
  success: boolean;
  subscriber_count: number;
}

// === Community Plans ===

export function getCommunityPlans(params?: PlansFilterParams): Promise<CommunityPlanListItem[]> {
  const query = params ? '?' + new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null) as [string, string][]
  ).toString() : '';
  return fetchApi(`/api/v1/plans/community/${query}`);
}

export function getPlanDetail(id: number): Promise<CommunityPlanDetail> {
  return fetchApi(`/api/v1/plans/community/${id}/`);
}

export function getPlanDetailBySlug(slug: string): Promise<CommunityPlanDetail> {
  return fetchApi(`/api/v1/plans/community/by-slug/${slug}/`);
}

export function getCrowdWisdom(id: number): Promise<CrowdWisdom> {
  return fetchApi(`/api/v1/plans/community/${id}/crowd-wisdom/`);
}

export function forkPlan(id: number, startDate?: string): Promise<Itinerary> {
  return fetchApi(`/api/v1/plans/community/${id}/fork/`, {
    method: 'POST',
    body: JSON.stringify(startDate ? { start_date: startDate } : {}),
  });
}

export function markHelpful(id: number): Promise<{ status: string }> {
  return fetchApi(`/api/v1/plans/community/${id}/mark-helpful/`, { method: 'POST' });
}

export function voteActivity(
  planId: number,
  activityTitle: string,
  dayNumber: number,
  sentiment: 'interested' | 'not_for_me',
  sessionId: string,
): Promise<{ status: string; sentiment: string }> {
  return fetchApi(`/api/v1/plans/community/${planId}/vote/`, {
    method: 'POST',
    body: JSON.stringify({ activity_title: activityTitle, day_number: dayNumber, sentiment, session_id: sessionId }),
  });
}

export interface VoteSummary {
  total_voters: number;
  top_pick: string | null;
  most_debated: string | null;
  activities: Record<string, { interested: number; not_for_me: number }>;
}

export function getVoteSummary(planId: number): Promise<VoteSummary> {
  return fetchApi(`/api/v1/plans/community/${planId}/votes/`);
}

// === Collections ===

export interface CollectionSummary {
  id: number;
  slug: string;
  title: string;
  description: string;
  plan_count: number;
}

export interface CollectionDetail extends CollectionSummary {
  creator_name: string;
  creator_username: string;
  plans: Array<{
    id: number;
    title: string;
    slug: string;
    destination: string;
    duration_days: number;
    group_type: string;
    /** Real uses: public, live copies; 0 renders nothing. */
    used_count: number;
  }>;
}

export function getCreatorCollections(username: string): Promise<{ collections: CollectionSummary[] }> {
  return fetchApi(`/api/v1/plans/collections/by/${username}/`);
}

export function getCollection(id: number): Promise<CollectionDetail> {
  return fetchApi(`/api/v1/plans/collections/${id}/`);
}

export function forkAllCollection(id: number, startDate: string): Promise<{ created: number[]; count: number }> {
  return fetchApi(`/api/v1/plans/collections/${id}/fork-all/`, {
    method: 'POST',
    body: JSON.stringify({ start_date: startDate }),
  });
}

export function suggestActivity(
  planId: number,
  data: { title: string; location?: string; time_preference?: string; note?: string },
): Promise<{ id: number; title: string; status: string }> {
  return fetchApi(`/api/v1/plans/community/${planId}/suggest/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// === Discovery Feeds ===

export function getTrending(): Promise<DestinationIntel[]> {
  return fetchApi('/api/v1/plans/trending/');
}

export function getPerfectTiming(): Promise<DestinationIntel[]> {
  return fetchApi('/api/v1/plans/perfect-timing/');
}

/** One starter plan on the anonymous first screen (Almanac D12). Text only:
 *  the endpoint carries no image, so no credit is owed. `provenance` is the
 *  creator's display name, '' when the plan has no creator. `times_used`
 *  counts the plan's public, live copies (auto-starters decision 2): mostly 0,
 *  which the first screen hides. The server chooses and rotates the list. */
export interface StarterPlan {
  slug: string;
  title: string;
  hook: string;
  provenance: string;
  times_used: number;
}

export function getStarters(): Promise<StarterPlan[]> {
  return fetchApi('/api/v1/plans/starters/');
}

export function getFeatured(): Promise<CommunityPlanDetail | null> {
  return fetchApi('/api/v1/plans/featured/');
}

export function getIntel(destination: string): Promise<DestinationIntel> {
  return fetchApi(`/api/v1/plans/intel/${encodeURIComponent(destination)}/`);
}

export interface IntelBulletsResponse {
  destination: string;
  bullets: Array<{ icon: string; text: string }>;
  is_plus: boolean;
}

// === Photo Pool (browse images) ===

export interface PhotoPoolItem {
  id: number;
  url: string;
  thumb_url: string;
  photographer: string;
  unsplash_link: string;
}

export interface PhotoPoolResponse {
  query_key: string;
  default_index: number;
  server_date: string;
  photos: PhotoPoolItem[];
}

export function getPhotoPool(queryKey: string): Promise<PhotoPoolResponse> {
  return fetchApi(`/api/v1/plans/photos/?query_key=${encodeURIComponent(queryKey)}`);
}

export function getIntelBullets(destination: string): Promise<IntelBulletsResponse> {
  return fetchApi(`/api/v1/plans/intel-bullets/${encodeURIComponent(destination)}/`);
}

// === Intel Subscription ===

export function intelSubscribe(data: IntelSubscribeData): Promise<IntelSubscribeResponse> {
  return fetchApi('/api/v1/plans/intel-subscribe/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getIntelSubscriberCount(): Promise<{ count: number }> {
  return fetchApi('/api/v1/plans/intel-subscribe/count/');
}

export function intelUnsubscribe(token: string): Promise<{ success: boolean }> {
  return fetchApi(`/api/v1/plans/intel-unsubscribe/${token}/`, { method: 'DELETE' });
}

// === Community Share (post-trip) ===

/** The publish status the server derives for one of the caller's own
 *  itineraries (Phase 2): `shared` = promoted OR a live candidate;
 *  `publish.state` is one of none | publishing | awaiting_owner | needs_fix |
 *  incomplete | published | withdrawn | unpublished. */
export interface PublishProblem {
  check: string;
  label: string;
  detail: string;
}

export interface PublishStatus {
  state: string;
  /** The FACT — in Explore right now — beside `state`, the NEWS. */
  published: boolean;
  reason: string;
  checks: Record<string, string>;
  plan_id: number | null;
  promoted_at: string | null;
  first_publish: boolean | null;
  /** The failed checks in the sharer's language (increment 5): one per
   *  check that read `failed`, only in `needs_fix`; `[]` otherwise, always
   *  present. `detail` is the sweep's own sentence; the evidence never
   *  leaves the server. */
  problems: PublishProblem[];
}

export function checkShareStatus(
  itineraryId: number,
): Promise<{ shared: boolean; edit_count: number; publish: PublishStatus }> {
  return fetchApi(`/api/v1/plans/community/share/?itinerary_id=${itineraryId}`);
}

export function shareTripToCommunity(
  itineraryId: number,
): Promise<{ candidate_id: number; state: string }> {
  return fetchApi('/api/v1/plans/community/share/', {
    method: 'POST',
    body: JSON.stringify({ itinerary_id: itineraryId }),
  });
}
